import dictionary from '@dictionaries/dictionary';
import mixin from '@mixins/mixin';
import store from '@store/index';
import Particles from '@tsparticles/vue2';
import axios from 'axios';
import * as GmapVue from 'gmap-vue'; // https://diegoazh.github.io/gmap-vue/#about-gmapvue
import { loadFull } from 'tsparticles';
import VeeValidate, { Validator } from 'vee-validate';
import Vue from 'vue';
import VueAxios from 'vue-axios';
import VueRellax from 'vue-rellax';

import AppHeader from '@components/Header/Header.vue';
import Homepage from '@pages/Home/Home.vue';

import AuthResetPassword from '@components/Auth/ResetPassword.vue';

import E403 from '@components/partials/error/E403.vue';
import E404 from '@components/partials/error/E404.vue';
import E406 from '@components/partials/error/E406.vue';
import E500 from '@components/partials/error/E500.vue';

import router from './router';

Validator.extend('alphanumspace', {
    getMessage: (field) => `Invalid ${field}.`,
    validate: (value) => /^[\w\-\s]+$/.test(value),
});

Validator.extend('alphaspace', {
    getMessage: (field) => `Invalid ${field}.`,
    validate: (value) => /^[a-z ,.'-]+$/i.test(value),
});

Vue.use(VueAxios, axios);
Vue.use(VeeValidate, {
    inject: false,
    dictionary,
});

Vue.use(GmapVue, {
    load: {
        key: process.env.MIX_GOOGLE_MAPS_API_KEY,
        libraries: 'places,geocoding',
    },
    installComponents: false,
});

Vue.use(VueRellax);

Vue.use(Particles, {
    init: async (engine) => {
        await loadFull(engine);
    },
});

Vue.filter('date', (value) => {
    if (!value) {
        return 'Select date';
    }
    const options = {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    };
    return new Date(value).toLocaleDateString(undefined, options);
});

Vue.mixin(mixin);

const Login = () => import('@components/Auth/Login.vue');
const CreateUser = () => import('@components/User/CreateUser.vue');
const AdsSearch = () => import('@components/Search/AdsSearch.vue');
const AdsSearchMap = () => import('@components/Search/AdsSearchMap.vue');
const AdForm = () => import('@components/AdForm/AdForm.vue');
const SelectRentType = () => import('@components/AdForm/SelectRentType.vue');
const AdDetails = () => import('@components/AdDetails/AdDetails.vue');
const AdvertsPostedBy = () => import('@components/AdvertsPostedBy/AdvertsPostedBy.vue');
const UserFavorites = () => import('@components/UserFavorites/UserFavorites.vue');
const UserAdverts = () => import('@components/UserAdverts/UserAdverts.vue');
const UserAdAlerts = () => import('@components/UserAdAlerts/UserAdAlerts.vue');
const UserIntegrations = () => import('@components/UserIntegrations/UserIntegrations.vue');
const ProspectingTool = () => import('@components/ProspectingTool/ProspectingTool.vue');
const TenantedProperties = () => import('@components/TenantedProperties/TenantedProperties.vue');
const ProspectingDatabase = () => import('@components/ProspectingDatabase/ProspectingDatabase.vue');
const LandLocator = () => import('@components/LandLocator/LandLocator.vue');
const ProspectingDatabaseUploadDetails = () => import('@components/ProspectingDatabase/ProspectingDatabaseUploadDetails.vue');
const AgentsPage = () => import('@pages/static/Agent/AgentsPage.vue');
const LandlordPage = () => import('@pages/static/Landlord/LandlordPage.vue');
const PostAdPage = () => import('@pages/static/PostAd/PostAdPage.vue');
const RenterPage = () => import('@pages/static/Renter/RenterPage.vue');
const TenantPage = () => import('@pages/static/Tenant/TenantPage.vue');
const AboutPage = () => import('@pages/static/About/AboutPage.vue');
const AutomatedDataFeedPage = () => import('@pages/static/DataFeed/AutomatedDataFeedPage.vue');
const AcceptableUsePolicyPage = () => import('@pages/static/AcceptableUsePolicy/AcceptableUsePolicyPage.vue');
const FaqPage = () => import('@pages/static/FAQ/FaqPage.vue');
const FaqQuestionsPage = () => import('@pages/static/FAQ/FaqQuestionsPage.vue');
const TenantsGuidePage = () => import('@pages/static/Tenant/TenantsGuidePage.vue');
const WhyKrispyPage = () => import('@pages/static/WhyKrispy/WhyKrispyPage.vue');
const PrivacyPolicyPage = () => import('@pages/static/PrivacyPolicy/PrivacyPolicyPage.vue');
const TermsAndConditionsPage = () => import('@pages/static/TermsAndConditions/TermsAndConditionsPage.vue');
const AgentTermsAndConditionsPage = () => import('@pages/static/TermsAndConditions/AgentTermsAndConditionsPage.vue');
const GetInTouchForm = () => import('@components/GetInTouchForm/GetInTouchForm.vue');
const Sitemap = () => import('@pages/static/Sitemap/Sitemap.vue');
const Dashboard = () => import('@components/Dashboard/Dashboard.vue');
const TenantReferencing = () => import('@pages/TenantReferencing/TenantReferencing.vue');
const LeadManager = () => import('@pages/LeadManager/LeadManager.vue');
const CompanySearch = () => import('@pages/CompanySearch/CompanySearch.vue');
const ValuationPage = () => import('@pages/Valuation/ValuationPage.vue');
const MultibranchLayout = () => import('@pages/MultibranchAdmin/partials/MultibranchLayout.vue');
const MultibranchDashboard = () => import('@pages/MultibranchAdmin/Dashboard/Dashboard.vue');
const MultibranchBilling = () => import('@pages/MultibranchAdmin/Billing/Billing.vue');
const DashboardLayout = () => import('@pages/partials/DashboardLayout.vue');
const BookADemoPage = () => import('@pages/BookADemo/BookADemoPage.vue');
const PricingPage = () => import('@pages/Pricing/PricingPage.vue');
const LetterSendingPage = () => import('@pages/LetterSending/LetterSendingPage.vue');
const LetterSendingOverviewPage = () => import('@pages/LetterSending/LetterSendingOverviewPage.vue');
const PurchaseCredits = () => import('@pages/PurchaseCredits/PurchaseCredits.vue');

const vm = new Vue({
    el: '#app',
    components: {
        Login,
        CreateUser,
        AppHeader,
        Homepage,
        AdsSearch,
        AdsSearchMap,
        AdForm,
        SelectRentType,
        AdDetails,
        AdvertsPostedBy,
        UserFavorites,
        UserAdverts,
        UserAdAlerts,
        UserIntegrations,
        ProspectingTool,
        TenantedProperties,
        ProspectingDatabase,
        ProspectingDatabaseUploadDetails,
        LandLocator,
        AuthResetPassword,
        AgentsPage,
        LandlordPage,
        PostAdPage,
        RenterPage,
        TenantPage,
        AboutPage,
        AutomatedDataFeedPage,
        AcceptableUsePolicyPage,
        FaqPage,
        FaqQuestionsPage,
        TenantsGuidePage,
        WhyKrispyPage,
        PrivacyPolicyPage,
        TermsAndConditionsPage,
        AgentTermsAndConditionsPage,
        GetInTouchForm,
        Sitemap,
        Dashboard,
        TenantReferencing,
        LeadManager,
        CompanySearch,
        ValuationPage,
        MultibranchLayout,
        MultibranchDashboard,
        MultibranchBilling,
        DashboardLayout,
        BookADemoPage,
        PricingPage,
        LetterSendingPage,
        LetterSendingOverviewPage,
        PurchaseCredits,
        E404,
        E403,
        E500,
        E406,
    },
    store,
    router,
});

export default vm;
